import React, { useMemo, useState } from 'react';
import { createAvatar } from '@dicebear/core';
import { bottts } from '@dicebear/collection';

// Cada PERFIL (não cada bot) tem uma "cara" fixa (seed = grupo/perfil, ex.
// "conservative" — o mesmo em todos os timeframes que esse perfil opera, já
// que hoje 1 perfil = vários bots, um por TF, e devem parecer o mesmo
// personagem) gerada via DiceBear (SVG, sem asset baixado da internet).
//
// Dois sinais diferentes sobre o personagem, de fontes diferentes:
//   - "mood" (humor) = tendência recente (PnL + streak, calculado a cada
//     tick em ai_service/app/mood.py) — muda rápido, sobe e desce.
//   - "health" (ferimento) = capital atual / capital inicial — estado
//     ACUMULADO, só piora de verdade com prejuízo real e só melhora se o
//     bot se recuperar. "Dinheiro é a vida dele" — perder capital deixa
//     marca visível (curativo, rachadura), não é resetado a cada tick.
// Nenhum dos dois muda o corpo DiceBear em si (fixo por perfil) — mood
// muda o selo de expressão, health sobrepõe curativos/rachaduras.

const MOOD_STYLE = {
  on_fire: { ring: '#fb923c', label: 'Em chamas', anim: 'bc-bounce', react: '🔥 Bombando!' },
  happy:   { ring: '#34d399', label: 'Feliz',      anim: 'bc-bob',    react: '😊 Indo bem!' },
  neutral: { ring: '#94a3b8', label: 'Neutro',     anim: 'bc-bob',    react: '😐 De boa.' },
  sad:     { ring: '#fbbf24', label: 'Triste',     anim: 'bc-shiver', react: '😟 Meio duro...' },
  critical:{ ring: '#f87171', label: 'Mal',        anim: 'bc-shiver', react: '🤒 Precisando de ajuda!' },
};

// Expressão de verdade: o próprio DiceBear (estilo bottts) tem variações de
// olhos/boca escolhíveis -- em vez de um selo separado no canto, a cara do
// personagem É gerada com a combinação certa pro humor. Escolhidas olhando
// lado a lado todas as combinações disponíveis:
//   on_fire = olhos de coração + sorriso largo (empolgado)
//   happy   = olhos "happy" (sobrancelha curva) + sorriso
//   neutral = olhos redondos simples + boca discreta
//   sad     = olhos arregalados (tensos) + boca reta/tensa
//   critical= olhos em X (desmaiado) + boca cerrada
const MOOD_FACE = {
  on_fire:  { eyes: 'hearts',  mouth: 'smile02' },
  happy:    { eyes: 'happy',   mouth: 'smile01' },
  neutral:  { eyes: 'round',   mouth: 'diagram' },
  sad:      { eyes: 'bulging', mouth: 'square02' },
  critical: { eyes: 'dizzy',   mouth: 'bite' },
};
// Sem posição aberta = dormindo, esperando a próxima entrada -- estado de
// ATIVIDADE, sobrepõe a cara do humor (que continua valendo pro anel/reação).
const SLEEP_FACE = { eyes: 'eva', mouth: 'diagram' };

// health = capital / capital_inicial. >=0.95 saudável, e vai acumulando
// marca visível conforme o prejuízo aumenta -- ferimentos leves primeiro
// (arranhão), depois curativo, depois múltiplos + rachadura + "combalido".
function woundTier(health) {
  if (health >= 0.95) return 0;
  if (health >= 0.75) return 1;   // arranhão leve
  if (health >= 0.5)  return 2;   // 1 curativo
  if (health >= 0.25) return 3;   // 2 curativos + rachadura pequena
  return 4;                       // combalido: curativos + rachadura grande + sem cor
}

function Bandage({ x, y, rotate = 0 }) {
  return (
    <g transform={`translate(${x},${y}) rotate(${rotate})`}>
      <rect x="-5" y="-2.5" width="10" height="5" rx="1.5" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="0.4" />
      <line x1="-1.6" y1="-2.5" x2="-1.6" y2="2.5" stroke="#cbd5e1" strokeWidth="0.5" />
      <line x1="1.6" y1="-2.5" x2="1.6" y2="2.5" stroke="#cbd5e1" strokeWidth="0.5" />
    </g>
  );
}

function Scratch({ x, y, rotate = 0 }) {
  return (
    <g transform={`translate(${x},${y}) rotate(${rotate})`} stroke="#7f1d1d" strokeWidth="0.8" strokeLinecap="round">
      <line x1="-4" y1="-3" x2="4" y2="3" />
      <line x1="-2" y1="-4" x2="6" y2="4" opacity="0.6" />
    </g>
  );
}

function WoundOverlay({ tier }) {
  if (tier === 0) return null;
  return (
    <svg viewBox="0 0 44 44" width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
      {tier >= 1 && <Scratch x={32} y={12} rotate={20} />}
      {tier >= 2 && <Bandage x={12} y={10} rotate={-15} />}
      {tier >= 3 && <Bandage x={30} y={30} rotate={25} />}
      {tier >= 4 && (
        <path d="M22,4 L19,16 L24,20 L18,32" stroke="#450a0a" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.85" />
      )}
    </svg>
  );
}

// Estrela de "veterano" -- prestígio por experiência acumulada (nº de
// trades), independente de humor/saúde: sobrevive muito tempo = medalha.
function veteranTier(totalTrades) {
  if (totalTrades >= 100) return { color: '#fbbf24', label: 'Veterano ouro (100+ trades)' };
  if (totalTrades >= 50) return { color: '#cbd5e1', label: 'Veterano prata (50+ trades)' };
  if (totalTrades >= 10) return { color: '#d97706', label: 'Veterano bronze (10+ trades)' };
  return null;
}

function VeteranBadge({ totalTrades }) {
  const v = veteranTier(totalTrades || 0);
  if (!v) return null;
  return (
    <div title={v.label} style={{
      position: 'absolute', top: -3, left: -3, width: 15, height: 15,
      borderRadius: '50%', background: '#0f172a', border: `1.5px solid ${v.color}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg viewBox="0 0 24 24" width="9" height="9">
        <path d="M12 2 L14.5 9 L22 9.5 L16 14.5 L18 22 L12 17.5 L6 22 L8 14.5 L2 9.5 L9.5 9 Z" fill={v.color} />
      </svg>
    </div>
  );
}

let injected = false;
function ensureKeyframes() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes bc-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
    @keyframes bc-bounce { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-7px) scale(1.05); } }
    @keyframes bc-shiver { 0%, 100% { transform: translateX(0) rotate(0deg); } 25% { transform: translateX(-1px) rotate(-2deg); } 75% { transform: translateX(1px) rotate(2deg); } }
    @keyframes bc-poke { 0% { transform: scale(1) rotate(0deg); } 30% { transform: scale(1.25) rotate(-8deg); } 60% { transform: scale(0.95) rotate(6deg); } 100% { transform: scale(1) rotate(0deg); } }
    @keyframes bc-sleep { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-1.5px) scale(1.02); } }
    @keyframes bc-zzz { 0% { transform: translateY(0); opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { transform: translateY(-10px); opacity: 0; } }
  `;
  document.head.appendChild(style);
}

export default function BotCharacter({ seed, mood = 'neutral', health = 1, totalTrades = 0, hasPosition = false, size = 56 }) {
  ensureKeyframes();
  const [poking, setPoking] = useState(false);
  const [showReact, setShowReact] = useState(false);
  const m = MOOD_STYLE[mood] || MOOD_STYLE.neutral;
  const tier = woundTier(health);
  const sleeping = !hasPosition;

  const face = sleeping ? SLEEP_FACE : (MOOD_FACE[mood] || MOOD_FACE.neutral);
  const svg = useMemo(() => {
    const avatar = createAvatar(bottts, {
      seed: seed || 'bot',
      eyes: [face.eyes],
      mouth: [face.mouth],
      mouthProbability: 100,
      backgroundType: ['gradientLinear'],
      radius: 50,
    });
    return avatar.toString();
  }, [seed, face.eyes, face.mouth]);

  const handleClick = () => {
    setPoking(true);
    setShowReact(true);
    setTimeout(() => setPoking(false), 500);
    setTimeout(() => setShowReact(false), 1400);
  };

  const anim = poking ? 'bc-poke' : sleeping ? 'bc-sleep' : m.anim;
  const reactText = sleeping ? '👀 Esperando entrada...' : m.react;

  return (
    <div
      onClick={handleClick}
      title={`${sleeping ? 'Dormindo (sem posição aberta) · ' : ''}${m.label}${tier >= 4 ? ' · combalido (capital baixo)' : tier >= 2 ? ' · ferido' : ''} — clique pra cutucar`}
      style={{
        position: 'relative', width: size, height: size, cursor: 'pointer',
        borderRadius: '50%', border: `2px solid ${m.ring}`,
        boxShadow: `0 0 ${sleeping ? 4 : 8}px ${m.ring}${sleeping ? '33' : '55'}`,
        opacity: sleeping ? 0.75 : 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f172a', flexShrink: 0,
      }}
    >
      <div
        style={{
          width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden',
          animation: `${anim} ${poking ? '0.5s' : sleeping ? '3.2s' : '2.4s'} ease-in-out infinite`,
          animationIterationCount: poking ? 1 : 'infinite',
          filter: tier >= 4 ? 'grayscale(45%) brightness(0.8)' : 'none',
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {/* Ferimentos acumulados -- reflete capital perdido, não reseta com o tick */}
      <WoundOverlay tier={tier} />
      {/* Medalha de veterano -- experiência acumulada (nº de trades) */}
      <VeteranBadge totalTrades={totalTrades} />
      {/* "Zzz" flutuante -- sem posição aberta, esperando a próxima entrada */}
      {sleeping && !showReact && (
        <span style={{
          position: 'absolute', top: -6, right: -2, fontSize: Math.max(10, size * 0.2),
          animation: 'bc-zzz 2.6s ease-in-out infinite', pointerEvents: 'none',
        }}>💤</span>
      )}
      {showReact && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', border: `1px solid ${m.ring}`, borderRadius: 8,
          padding: '3px 8px', fontSize: 10, whiteSpace: 'nowrap', marginBottom: 4,
          color: '#e2e8f0', pointerEvents: 'none',
        }}>
          {reactText}
        </div>
      )}
    </div>
  );
}
